/**
 * Search Coordinator Module - Phase 3 Migration
 * Coordinates all search modules and provides main search interface
 */

"use strict";

// Check if debug mode is enabled (global)
const urlParams = new URLSearchParams(window.location.search);


// Import search modules
import { getMatchConditions, myEach, addCategoryMatchConditions } from './search-conditions.js';
import { fuzzySearch } from './fuzzy-search.js';
// typeahead removed

/**
 * Generate substring matcher function for typeahead
 * Migrated from main.js - Phase 3
 * Enhanced with TypeaheadSuggestionBuilder for HTML generation
 * @param {Array} strs - Array of file objects
 * @returns {Function} Function that performs matching and returns HTML
 */
export function substringMatcher(strs) {
    let retar = function findMatches(q, cb) {
        console.log('🎯 [FILTER TRACE] substringMatcher CALLED with query:', q, 'on', strs.length, 'files');
        if (debugMode === '1') {
                console.log('🔍 [substringMatcher] Starting search for:', q, 'Files count:', strs.length);
            }
        if (window.l) window.l(q);
        
        if (window.HyperLog) {
            HyperLog.debug("Starting search", { 
                query: q, 
                filesCount: strs.length, 
                searchMode: window.searchMode 
            });
        }
        
        // Обновляем поисковые условия при каждом вызове
        if (window.cachedRequest) {
            window.cachedRequest['mCond'] = getMatchConditions(q);
            addCategoryMatchConditions(window.cachedRequest['mCond']);
        }
        
        let matchConditions = window.cachedRequest ? window.cachedRequest['mCond'] : [];
        
        if (window.HyperLog) {
            HyperLog.debug("Search conditions prepared", { 
                conditionsCount: matchConditions.length,
                conditions: matchConditions
            });
        }
        
        var matches, searchProgressLatestInFiles = 33;
        
        // Единый проход поиска: используем full path сразу (path+name)
        console.log('[substringMatcher] Unified search (full path) start');
        matches = myEach(strs, matchConditions, true, false);
        console.log('[substringMatcher] Unified search (full path) end, matches=', matches.length);
        searchProgressLatestInFiles = matches.length > 0 ? 67 : searchProgressLatestInFiles;
        if (window.HyperLog) {
            HyperLog.debug("Unified search completed", { matchesFound: matches.length, searchType: "full_path" });
        }
        
        // Обновляем прогресс поиска
        if (matches.length > 0) {
            // Fuzzy reranking before UI rendering
            try {
                if (window.Fuse && fuzzySearch && typeof fuzzySearch.sortByRelevance === 'function') {
                    const beforeCount = matches.length;
                    const __t0 = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
                    matches = fuzzySearch.sortByRelevance(q, matches, {
                        keys: ['name', 'path', 'n', 'p'],
                        threshold: (window.fuzzyOptions && typeof window.fuzzyOptions.threshold === 'number') ? window.fuzzyOptions.threshold : 0.3
                    });
                    const __t1 = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
                    console.log('[substringMatcher] Fuzzy rerank applied:', { beforeCount, afterCount: matches.length, ms: Number((__t1 - __t0).toFixed(2)) });
                } else if (debugMode === '1') {
                    console.log('[substringMatcher] Fuzzy rerank skipped (Fuse or API missing)');
                }
            } catch (e) {
                console.warn('[substringMatcher] Fuzzy rerank error:', e);
            }
            if (window.searchMode != "fulltext") { 
                updateSearchProgress(searchProgressLatestInFiles);
            }
            
            if (debugMode === '1') {
                console.log('🎨 [substringMatcher] Converting search results to HTML templates...');
            }
            if (debugMode === '1') {
                console.log('🎨 [substringMatcher] Raw matches received:', matches.length, 'matches');
            }
            
            const htmlResults = matches.map((match, index) => {
                try {
                    // CRITICAL: Validate match data structure
                    if (!match || typeof match !== 'object') {
                        if (debugMode === '1') {
                console.error(`[substringMatcher] Invalid match at index ${index}:`, match);
            }
                        return `<div class="suggestion-error">Invalid data: ${JSON.stringify(match)}</div>`;
                    }
                    
                    // Детальное логирование исходных данных
                    if (index < 3) {
                        console.log(`[substringMatcher] Processing match ${index}:`, {
                            match: match,
                            name: match.name,
                            path: match.path,
                            size: match.size,
                            hasName: match.name !== undefined,
                            hasPath: match.path !== undefined,
                            hasSize: match.size !== undefined,
                            matchKeys: Object.keys(match)
                        });
                    }
                    
                    // CRITICAL: Ensure all required fields are present
                    const templateData = {
                        name: match.name || match.n || 'Unknown',
                        path: match.path || match.p || '',
                        size: match.size || match.s || 0,
                        fullMode: match.fullMode || false
                    };
                    
                    // Проверяем какая функция используется
                    if (index < 1) {
                        if (debugMode === '1') {
                console.log('[substringMatcher] Using generateSuggestionTemplate:', typeof typeaheadSuggestionBuilder.generateSuggestionTemplate);
            }
                        if (debugMode === '1') {
                console.log('[substringMatcher] Template data being passed:', templateData);
            }
                    }
                    
                    const html = typeaheadSuggestionBuilder.generateSuggestionTemplate(templateData);
                    
                    if (index < 3) { // Логируем первые 3 для отладки
                        if (debugMode === '1') {
                console.log(`[substringMatcher] Generated template for: ${match.name}, length: ${html.length}`);
            }
                        if (html.includes('Template Error')) {
                            if (debugMode === '1') {
                console.error('[substringMatcher] TEMPLATE ERROR detected in result:', html);
            }
                        }
                    }
                    
                    return html;
                    
                } catch (error) {
                    if (debugMode === '1') {
                console.error('[substringMatcher] Error generating template for:', match.name, error);
            }
                    return `<div class="suggestion-error">Error: ${match.name}</div>`;
                }
            });
            
            if (window.HyperLog) {
                HyperLog.debug("HTML templates generated", { 
                    resultCount: htmlResults.length, 
                    limitReached: matches.length === (window.SEARCH_LIMIT || 300),
                    searchProgressLatestInFiles
                });
            }
            
            if (debugMode === '1') {
                console.log('📤 [substringMatcher] Returning', htmlResults.length, 'HTML templates to typeahead');
            }
            if (debugMode === '1') {
                console.log('📤 [substringMatcher] Calling callback with results...');
            }
            cb(htmlResults);
            if (debugMode === '1') {
                console.log('✅ [substringMatcher] Callback called successfully');
            }
            
        } else {
            searchProgressLatestInFiles = 100;
            updateSearchProgress(searchProgressLatestInFiles);
            
            if (window.HyperLog) {
                HyperLog.debug("No matches found", { 
                    query: q, 
                    searchProgressLatestInFiles 
                });
            }
            
            if (debugMode === '1') {
                console.log('[substringMatcher] No matches found for:', q);
            }
            cb([]);
        }

        // Обновляем счетчик результатов
        console.log('🎯 [FILTER TRACE] substringMatcher about to update UI with', matches.length, 'matches');
        const SEARCH_LIMIT = window.SEARCH_LIMIT || 300;
        const hasMore = matches.length === SEARCH_LIMIT;
        updateResultsCount(matches.length, hasMore);
        
        // CRITICAL: Update main UI file count
        if (window.FileList && typeof window.FileList.updateResultsCount === 'function') {
            console.log('🎯 [FILTER TRACE] Updating main UI file count with:', matches.length);
            window.FileList.updateResultsCount(matches.length, hasMore);
        } else {
            console.warn('🎯 [FILTER TRACE] FileList.updateResultsCount not available');
        }
        
        if (window.INITIALIZED2) {
            window.INITIALIZED2 = true;
        }
    };
    
    return retar;
}

/**
 * Update search progress (placeholder implementation)
 * @param {number} progress - Progress value
 */
function updateSearchProgress(progress) {
    if (window.searchProgress) {
        window.searchProgress(progress / 100);
    }
}

/**
 * Update results count (placeholder implementation)  
 * @param {number} count - Results count
 */
function updateResultsCount(count, hasMore = false) {
    // Update UI with results count
    const resultsElement = document.querySelector('#search-results-count');
    if (resultsElement) {
        const SEARCH_LIMIT = window.SEARCH_LIMIT || 300;
        const showPlus = hasMore || (count === SEARCH_LIMIT);
        resultsElement.textContent = showPlus ? `${count}+` : count.toString();
    }
}

/**
 * Initialize search coordinator
 */
export function initializeSearchCoordinator() {
    // Search Coordinator initialization logging only in debug mode
}

// Global exports for backward compatibility
window.substringMatcher = substringMatcher;

// Auto-initialize
if (typeof window !== 'undefined') {
    initializeSearchCoordinator();
} 